import { NavLink, Outlet } from 'react-router-dom';
import {
  Activity,
  Bug,
  Database,
  Home,
  ImageIcon,
  LayoutGrid,
  LineChart,
  LogOut,
  Mail,
  UserCircle,
  Users,
} from 'lucide-react';
import { signOut } from 'aws-amplify/auth';
import { toast } from 'sonner';
import { cn } from '../../lib/cn';
import config from '../../lib/config';

const nav = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/challenges/view', label: 'Challenges', icon: LayoutGrid },
  { to: '/challenges/create', label: 'Create challenge', icon: Activity },
  { to: '/homepage', label: 'Homepage order', icon: Home },
  { to: '/carousel', label: 'Carousel', icon: ImageIcon },
  { to: '/metrics', label: 'Metrics', icon: LineChart },
  { to: '/subscriptions', label: 'Subscriptions', icon: Users },
  { to: '/appdata', label: 'App data', icon: Database },
  { to: '/bugs', label: 'Bugs', icon: Bug },
  { to: '/interest', label: 'Interest', icon: Mail },
  { to: '/unsubscribes', label: 'Unsubscribes', icon: Mail },
  { to: '/user', label: 'User lookup', icon: UserCircle },
];

export default function AppShell() {
  async function handleSignOut() {
    try {
      await signOut();
      toast.success('Signed out');
      window.location.href = '/login';
    } catch (e) {
      toast.error(String(e));
    }
  }

  return (
    <div className="flex min-h-screen">
      <aside
        className="flex w-64 shrink-0 flex-col"
        style={{ backgroundColor: '#2A3746' }}
      >
        {/* Logo / brand */}
        <div className="flex items-center gap-3 px-4 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <img
            src="/icon.png"
            alt=""
            className="h-9 w-9 shrink-0 rounded-xl object-contain"
          />
          <div className="min-w-0">
            <p className="mb-0 text-xs font-semibold uppercase tracking-wide leading-none" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Challenge Accepted
            </p>
            <p className="mb-0 mt-1 text-sm font-semibold text-white leading-none">
              CMS{config.env ? ` · ${config.env}` : ''}
            </p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-3">
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'text-white'
                    : 'text-white/55 hover:text-white/90',
                )
              }
              style={({ isActive }) =>
                isActive ? { backgroundColor: 'rgba(255,255,255,0.12)' } : undefined
              }
            >
              <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Sign out */}
        <div className="p-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button
            type="button"
            onClick={handleSignOut}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
            style={{ color: 'rgba(255,255,255,0.45)' }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(189,71,86,0.2)';
              (e.currentTarget as HTMLButtonElement).style.color = '#f87171';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = '';
              (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.45)';
            }}
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <main className="flex-1 px-8 py-8">
          <div className="mx-auto max-w-6xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
